import React from 'react';

import { FileUpload } from '@mui/icons-material';
import type { CardProps } from '@mui/material';
import { Alert, Box, Card, Collapse, Stack, useTheme } from '@mui/material';
import { get, includes } from 'lodash';
import { joinPairs } from '../utils/libs';
import { LoadingButton } from './loading_button';
import { apiAxios, apiAxiosAuth } from './use_http_request';

const defaultAspectRatio = 3 / 2;

export type UploadImagePickerCardProps = {
  initialSrc?: string | undefined;
  buttonText?: string | undefined;
  buttonUploadingText?: string | undefined;
  errorMessage?: string | undefined;
  noAuthUpload?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, image?: File) => void;
  onUploadDone?: (token: string) => void;
  onError?: (message?: string, error?: unknown) => void;
} & CardProps;

export const UploadImagePickerCard = ({
  onChange,
  onUploadDone,
  onError,
  initialSrc,
  buttonText,
  buttonUploadingText,
  errorMessage,
  noAuthUpload,
  sx,
  ...props
}: UploadImagePickerCardProps) => {
  const theme = useTheme();

  const abortControllerRef = React.useRef<AbortController | null>(null);

  const [image, setImage] = React.useState<File | null>(null);
  const imageUri = React.useMemo(
    () => (image instanceof File ? URL.createObjectURL(image) : initialSrc),
    [image, initialSrc],
  );

  const [isUploading, setIsUploading] = React.useState(false);
  const [errorMessageInternal, setErrorMessageInternal] = React.useState<string | null>(null);
  const setErrorMessage = React.useCallback(
    (nextErrorMessage: string | null, error?: unknown) => {
      setErrorMessageInternal(nextErrorMessage);
      if (onError) onError(nextErrorMessage ?? get(error, 'message'), error);
    },
    [setErrorMessageInternal, onError],
  );
  React.useEffect(() => {
    setErrorMessageInternal(errorMessage ?? null);
  }, [errorMessage]);

  const uploadImage = async (_image: File) => {
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsUploading(true);
    setErrorMessageInternal(null);
    try {
      const formData = new FormData();
      formData.append('image', _image);

      const request = {
        headers: { 'Content-Type': 'multipart/form-data' },
        data: formData,
        signal: abortController.signal,
      };

      const { data } = noAuthUpload
        ? await apiAxios({ method: 'POST', url: 'image_service', ...request })
        : await apiAxiosAuth('POST', 'image_service', request);

      const success: boolean | undefined = get(data, 'success');
      const token: string | undefined = get(data, ['data', 'token']);

      if (success && token) {
        if (onUploadDone) onUploadDone(token);
      } else {
        const errors = get(data, 'errors');
        const nextErrorMessage = joinPairs(errors);
        setErrorMessage(nextErrorMessage);
        if (onError) onError(nextErrorMessage);
      }
    } catch (error) {
      const { response } = error as { response?: { data?: unknown } };
      const { data } = response || {};
      const errors = get(data, 'errors');
      if (errors) {
        const nextErrorMessage = joinPairs(errors);
        setErrorMessage(nextErrorMessage);
      } else {
        const nextErrorMessage = get(error, 'message');
        setErrorMessage(nextErrorMessage ?? null);
      }
      if (onError) onError(errorMessage ?? undefined, error);
    }
    setIsUploading(false);
  };

  const onImageChangeHandler: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    let nextImage;
    if (event.target.files && event.target.files[0]) {
      [nextImage] = event.target.files;
      if (includes(nextImage.type, 'image/')) {
        setImage(nextImage);
        uploadImage(nextImage);
      } else {
        setErrorMessage('Invalid Image Type');
      }
    }
    if (onChange) {
      onChange(event, nextImage);
    }
  };

  return (
    <Card {...props} sx={{ width: '100%', aspectRatio: defaultAspectRatio, ...sx }}>
      <Stack width="100%" height="100%" position="relative">
        {imageUri && (
          <Box
            component="img"
            src={imageUri}
            maxWidth="100%"
            sx={{ objectFit: 'contain', aspectRatio: get(sx, 'aspectRatio', defaultAspectRatio) }}
          />
        )}
        <Stack
          width="100%"
          height="100%"
          position="absolute"
          justifyContent="center"
          alignItems="center"
          gap={1}
          bgcolor="rgba(0,0,0,0.25)"
        >
          <LoadingButton
            id="input-image-upload"
            component="label"
            variant="contained"
            color="inherit"
            size="large"
            disableElevation
            loading={isUploading}
            disabled={isUploading}
            startIcon={<FileUpload />}
            sx={{ opacity: 0.85, minWidth: '40%' }}
            style={{ backgroundColor: theme.palette.background.paper }}
          >
            {isUploading ? buttonUploadingText ?? 'อัปโหลด' : buttonText ?? 'เลือกรูป'}
            <input
              id="input-image-upload"
              hidden
              type="file"
              accept="image/*"
              onClick={(e) => {
                // Clear current files selection. So same files will trigger onChange
                (e.target as HTMLInputElement).value = '';
              }}
              onChange={onImageChangeHandler}
            />
          </LoadingButton>

          <Collapse in={!!errorMessageInternal}>
            <Alert severity="error">{errorMessageInternal}</Alert>
          </Collapse>
        </Stack>
      </Stack>
    </Card>
  );
};
